import React, { useState, useEffect } from 'react';
import { taxonomyAPI, locationsAPI } from '../utils/api';
import toast from 'react-hot-toast';
import { Layers, Plus, X, GitBranch, ChevronRight, Hash, Type, MapPin, Building, Home } from 'lucide-react';

export default function TaxonomyPage() {
  const [natures, setNatures] = useState([]);
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [sites, setSites] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [activeTab, setActiveTab] = useState('taxonomy'); // 'taxonomy' | 'locations'
  const [activeNature, setActiveNature] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeSite, setActiveSite] = useState(null);

  const [showModal, setShowModal] = useState(null); // 'nature' | 'category' | 'type' | 'site' | 'location'
  const [form, setForm] = useState({ name: '', code: '', description: '', address: '', building: '', floor: '', area: '' });

  const loadAll = async () => {
    setLoading(true);
    try {
      const [nr, cr, tr, sr] = await Promise.all([
        taxonomyAPI.natures(),
        taxonomyAPI.categories(),
        taxonomyAPI.types(),
        locationsAPI.sites()
      ]);
      setNatures(nr.data.natures);
      setCategories(cr.data.categories);
      setTypes(tr.data.types);
      setSites(sr.data.sites);
    } catch { toast.error('Failed to load taxonomy'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const selectSite = async (site) => {
    setActiveSite(site);
    try {
      const res = await locationsAPI.siteLocations(site.id);
      setLocations(res.data.locations);
    } catch { toast.error('Failed to load locations'); }
  };

  const handleCreateNature = async () => {
    if (!form.name || !form.code) return toast.error('Name and code required');
    try {
      await taxonomyAPI.createNature(form);
      toast.success('Nature created');
      setShowModal(null);
      loadAll();
    } catch { toast.error('Failed to create nature'); }
  };

  const handleCreateCategory = async () => {
    if (!form.name || !activeNature) return toast.error('Name and nature required');
    try {
      await taxonomyAPI.createCategory({ ...form, nature_id: activeNature.id });
      toast.success('Category created');
      setShowModal(null);
      loadAll();
    } catch { toast.error('Failed to create category'); }
  };

  const handleCreateType = async () => {
    if (!form.name || !form.code || !activeCategory) return toast.error('Name, code and category required');
    try {
      await taxonomyAPI.createType({ ...form, category_id: activeCategory.id });
      toast.success('Asset type created');
      setShowModal(null);
      loadAll();
    } catch { toast.error('Failed to create type'); }
  };

  const handleCreateSite = async () => {
    if (!form.name || !form.code) return toast.error('Name and code required');
    try {
      await locationsAPI.createSite(form);
      toast.success('Site created');
      setShowModal(null);
      loadAll();
    } catch { toast.error('Failed to create site'); }
  };

  const handleCreateLocation = async () => {
    if (!form.name || !activeSite) return toast.error('Name and site required');
    try {
      await locationsAPI.createLocation({ ...form, site_id: activeSite.id });
      toast.success('Location created');
      setShowModal(null);
      selectSite(activeSite);
    } catch { toast.error('Failed to create location'); }
  };

  if (loading && natures.length === 0) return <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800 }}>Master Taxonomy</h1>
          <p style={{ fontSize: 14, color: 'var(--text-muted)' }}>Standardize asset identification and location profiling</p>
        </div>
        <div style={{ display: 'flex', background: 'var(--bg-muted)', padding: 4, borderRadius: 'var(--r-md)', border: '1px solid var(--border)' }}>
          <button onClick={() => setActiveTab('taxonomy')} style={{ 
            padding: '6px 16px', borderRadius: 'var(--r-sm)', border: 'none', cursor: 'pointer',
            background: activeTab === 'taxonomy' ? 'var(--bg-card)' : 'transparent',
            color: activeTab === 'taxonomy' ? 'var(--text)' : 'var(--text-muted)',
            fontWeight: 700, fontSize: 13, boxShadow: activeTab === 'taxonomy' ? 'var(--shadow-sm)' : 'none'
          }}>Categories</button>
          <button onClick={() => setActiveTab('locations')} style={{ 
            padding: '6px 16px', borderRadius: 'var(--r-sm)', border: 'none', cursor: 'pointer',
            background: activeTab === 'locations' ? 'var(--bg-card)' : 'transparent',
            color: activeTab === 'locations' ? 'var(--text)' : 'var(--text-muted)',
            fontWeight: 700, fontSize: 13, boxShadow: activeTab === 'locations' ? 'var(--shadow-sm)' : 'none'
          }}>Locations</button>
        </div>
      </div>

      {activeTab === 'taxonomy' ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, height: 'calc(100vh - 200px)' }}>
          {/* Tier 1: Natures */}
          <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="card-header" style={{ justifyContent: 'space-between' }}>
              <div className="card-title">1. Nature</div>
              <button className="btn btn-ghost btn-sm" onClick={() => { setForm({ name: '', code: '', description: '' }); setShowModal('nature'); }}>
                <Plus size={14} /> Add Nature
              </button>
            </div>
            <div className="card-body" style={{ overflowY: 'auto', padding: 0 }}>
              {natures.map(n => (
                <div key={n.id} onClick={() => { setActiveNature(n); setActiveCategory(null); }}
                  style={{
                    padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                    background: activeNature?.id === n.id ? 'var(--accent-muted)' : 'transparent',
                    borderLeft: activeNature?.id === n.id ? '4px solid var(--accent)' : '4px solid transparent',
                    transition: 'all 150ms'
                  }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: `${n.color}20`, color: n.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Layers size={16} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{n.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{n.code}</div>
                  </div>
                  <ChevronRight size={14} color="var(--text-muted)" />
                </div>
              ))}
            </div>
          </div>

          {/* Tier 2: Categories */}
          <div className="card" style={{ display: 'flex', flexDirection: 'column', opacity: activeNature ? 1 : 0.5 }}>
            <div className="card-header" style={{ justifyContent: 'space-between' }}>
              <div className="card-title">2. Category</div>
              {activeNature && (
                <button className="btn btn-ghost btn-sm" onClick={() => { setForm({ name: '', code: '', description: '' }); setShowModal('category'); }}>
                  <Plus size={14} /> Add Cat
                </button>
              )}
            </div>
            <div className="card-body" style={{ overflowY: 'auto', padding: 0 }}>
              {!activeNature ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Select Nature</div> : (
                categories.filter(c => c.nature_id === activeNature.id).map(c => (
                  <div key={c.id} onClick={() => setActiveCategory(c)}
                    style={{
                      padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                      background: activeCategory?.id === c.id ? 'var(--accent-muted)' : 'transparent',
                      borderLeft: activeCategory?.id === c.id ? '4px solid var(--accent)' : '4px solid transparent',
                    }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--bg-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Hash size={16} color="var(--text-secondary)" />
                    </div>
                    <div style={{ flex: 1 }}><div style={{ fontSize: 14, fontWeight: 600 }}>{c.name}</div></div>
                    <ChevronRight size={14} color="var(--text-muted)" />
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Tier 3: Types */}
          <div className="card" style={{ display: 'flex', flexDirection: 'column', opacity: activeCategory ? 1 : 0.5 }}>
            <div className="card-header" style={{ justifyContent: 'space-between' }}>
              <div className="card-title">3. Asset Type</div>
              {activeCategory && (
                <button className="btn btn-ghost btn-sm" onClick={() => { setForm({ name: '', code: '', description: '' }); setShowModal('type'); }}>
                  <Plus size={14} /> Add Type
                </button>
              )}
            </div>
            <div className="card-body" style={{ overflowY: 'auto', padding: 0 }}>
              {!activeCategory ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Select Category</div> : (
                types.filter(t => t.category_id === activeCategory.id).map(t => (
                  <div key={t.id} style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--bg-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Type size={16} color="var(--text-secondary)" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{t.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t.code}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) 2fr', gap: 20, height: 'calc(100vh - 200px)' }}>
          {/* Sites */}
          <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="card-header" style={{ justifyContent: 'space-between' }}>
              <div className="card-title"><Home size={16} style={{ marginRight:8 }} /> Sites</div>
              <button className="btn btn-ghost btn-sm" onClick={() => { setForm({ name: '', code: '', address: '' }); setShowModal('site'); }}>
                <Plus size={14} /> Add Site
              </button>
            </div>
            <div className="card-body" style={{ overflowY: 'auto', padding: 0 }}>
              {sites.map(s => (
                <div key={s.id} onClick={() => selectSite(s)}
                  style={{
                    padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                    background: activeSite?.id === s.id ? 'var(--accent-muted)' : 'transparent',
                    borderLeft: activeSite?.id === s.id ? '4px solid var(--accent)' : '4px solid transparent',
                  }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--bg-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Building size={16} color="var(--text-secondary)" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{s.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.code}</div>
                  </div>
                  <ChevronRight size={14} color="var(--text-muted)" />
                </div>
              ))}
            </div>
          </div>

          {/* Site Locations */}
          <div className="card" style={{ display: 'flex', flexDirection: 'column', opacity: activeSite ? 1 : 0.5 }}>
            <div className="card-header" style={{ justifyContent: 'space-between' }}>
              <div className="card-title"><MapPin size={16} style={{ marginRight:8 }} /> Locations in {activeSite?.name || '...'}</div>
              {activeSite && (
                <button className="btn btn-ghost btn-sm" onClick={() => { setForm({ name: '', building: '', floor: '', area: '' }); setShowModal('location'); }}>
                  <Plus size={14} /> Add Spot
                </button>
              )}
            </div>
            <div className="card-body" style={{ padding: 16 }}>
              {!activeSite ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Select a site to manage specific spots</div> : (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(240px, 1fr))', gap:12 }}>
                  {locations.map(loc => (
                    <div key={loc.id} style={{ padding:14, background:'var(--bg-muted)', borderRadius:'var(--r-md)', border:'1px solid var(--border)' }}>
                      <div style={{ fontSize:15, fontWeight:700, color:'var(--text)' }}>{loc.name}</div>
                      <div style={{ display:'flex', gap:8, marginTop:8 }}>
                        {loc.building && <span style={{ fontSize:10, padding:'2px 6px', background:'var(--bg-card)', borderRadius:4 }}>Bldg: {loc.building}</span>}
                        {loc.floor && <span style={{ fontSize:10, padding:'2px 6px', background:'var(--bg-card)', borderRadius:4 }}>Floor: {loc.floor}</span>}
                        {loc.area && <span style={{ fontSize:10, padding:'2px 6px', background:'var(--bg-card)', borderRadius:4 }}>Area: {loc.area}</span>}
                      </div>
                    </div>
                  ))}
                  {locations.length === 0 && <div style={{ gridColumn:'1/-1', textAlign:'center', padding:40, color:'var(--text-muted)', fontSize:13 }}>No spot locations added for this site yet.</div>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal modal-sm">
            <div className="modal-header">
              <div className="modal-title">Add {showModal}</div>
              <button className="btn-icon" onClick={() => setShowModal(null)}><X size={16} /></button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {(showModal === 'site' || showModal === 'nature' || showModal === 'category' || showModal === 'type') && (
                <div className="form-group">
                  <label className="form-label">Name</label>
                  <input className="form-input" value={form.name} onChange={e => setForm(f=>({...f,name:e.target.value}))} />
                </div>
              )}
              {showModal === 'site' && (
                <div className="form-group">
                  <label className="form-label">Address</label>
                  <input className="form-input" value={form.address} onChange={e => setForm(f=>({...f,address:e.target.value}))} />
                </div>
              )}
              {(showModal === 'site' || showModal === 'nature' || showModal === 'type') && (
                <div className="form-group">
                  <label className="form-label">Code</label>
                  <input className="form-input" value={form.code} onChange={e => setForm(f=>({...f,code:e.target.value.toUpperCase()}))} />
                </div>
              )}
              {showModal === 'location' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Location Name (e.g. Server Room)</label>
                    <input className="form-input" value={form.name} onChange={e => setForm(f=>({...f,name:e.target.value}))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Building / Tower</label>
                    <input className="form-input" value={form.building} onChange={e => setForm(f=>({...f,building:e.target.value}))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Floor</label>
                    <input className="form-input" value={form.floor} onChange={e => setForm(f=>({...f,floor:e.target.value}))} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Specific Area</label>
                    <input className="form-input" value={form.area} onChange={e => setForm(f=>({...f,area:e.target.value}))} />
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={
                showModal === 'nature' ? handleCreateNature : 
                showModal === 'category' ? handleCreateCategory : 
                showModal === 'type' ? handleCreateType :
                showModal === 'site' ? handleCreateSite : handleCreateLocation
              }>Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
